import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Card, Table, Button, Form, Input, Space, Select, Tag, Tooltip, Modal, message, Popconfirm, ColorPicker } from "antd";
import { SyncOutlined, EditOutlined, DeleteOutlined, PlusOutlined, BgColorsOutlined, RestOutlined, EyeOutlined } from "@ant-design/icons";
import moment from 'moment';

import { getAllCategoriesAdminDefault, updateCategoryAdmin, createCategoryAdmin, deleteCategoryAdmin, seedDefaultCategoriesAdmin } from '../../services/categoriesService';
import { RootState } from '../../app/store';
import './DefaultCategoryPage.scss';

const transactionTypes = [
    { value: "expense", label: "Expense" },
    { value: "income", label: "Income" },
    { value: "transfer", label: "Transfer" },
];

const languageOptions = [
    { value: 'vi', label: 'Vietnamese' },
    { value: 'en', label: 'English' },
    // Add more languages as needed
];

const DefaultCategoryPage = () => {
    const { token } = useSelector((state: RootState) => state.auth);
    const [categoryForm] = Form.useForm();

    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [colorPickerVisible, setColorPickerVisible] = useState(false);
    const [selectedColor, setSelectedColor] = useState('#1890ff');
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 10
    });
    const [translationFields, setTranslationFields] = useState([{ lang: 'vi', name: '', description: '' }, { lang: 'en', name: '', description: '' }]);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [detailCategory, setDetailCategory] = useState(null);

    useEffect(() => {
        fetchCategories(1, 10);
        // The initial load intentionally runs once on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchCategories = async (page = 1, limit = 10) => {
        try {
            setLoading(true);
            const payload = { page, limit, token };
            const response = await getAllCategoriesAdminDefault(payload);

            if (response.success) {
                setCategories(response.data.categories);
                setPagination(response.data.pagination);
            } else {
                message.error(response.message || "Failed to fetch categories");
            }
        } catch (error) {
            console.error("Failed to fetch categories:", error);
            message.error("Failed to fetch categories");
        } finally {
            setLoading(false);
        }
    };

    const handleAddCategory = () => {
        categoryForm.resetFields();
        setEditingCategory(null);
        setSelectedColor('#1890ff');
        setIsModalVisible(true);
    };

    // When editing, populate translation fields if available
    const handleEditCategory = (category) => {
        setEditingCategory(category);
        categoryForm.setFieldsValue({
            name: category.name,
            transactionType: category.transactionType,
            description: category.description,
            iconCode: category.iconCode || '',
            order: category.order || 0,
        });
        // Populate translations if present
        if (category.translations) {
            const translationsArr = Object.entries(category.translations).map(([lang, val]) => {
                const v = val as { name?: string; description?: string } | undefined;
                return {
                    lang,
                    name: v?.name || '',
                    description: v?.description || ''
                };
            });
            setTranslationFields(translationsArr.length > 0 ? translationsArr : [{ lang: 'vi', name: '', description: '' }, { lang: 'en', name: '', description: '' }]);
        } else {
            setTranslationFields([{ lang: 'vi', name: '', description: '' }, { lang: 'en', name: '', description: '' }]);
        }
        setSelectedColor(category.color || '#1890ff');
        setIsModalVisible(true);
    };

    const handleDeleteCategory = async (id) => {
        try {
            setLoading(true);
            const response = await deleteCategoryAdmin({ 
                categoryId: id, 
                token 
            });

            if (response.success) {
                message.success("Category deleted successfully");
                fetchCategories(pagination.currentPage, pagination.itemsPerPage);
            } else {
                message.error(response.message || "Failed to delete category");
            }
        } catch (error) {
            console.error("Failed to delete category:", error);
            message.error("Failed to delete category");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveCategory = async (values) => {
        try {
            setLoading(true);

            // Build translations object from translationFields
            const translations = {};
            translationFields.forEach(field => {
                if (field.lang) {
                    translations[field.lang] = {};
                    if (field.name) translations[field.lang].name = field.name;
                    if (field.description) translations[field.lang].description = field.description;
                }
            });

            const categoryData = {
                ...values,
                transactionType: values.transactionType
                    ? values.transactionType.charAt(0).toUpperCase() + values.transactionType.slice(1).toLowerCase()
                    : undefined,
                color: selectedColor,
                order: values.order ? Number(values.order) : 0,
                translations,
            };

            let response;

            if (editingCategory) {
                response = await updateCategoryAdmin({
                    categoryId: editingCategory._id,
                    categoryData,
                    token
                });
            } else {
                response = await createCategoryAdmin({
                    categoryData,
                    token
                });
            }

            if (response.success) {
                message.success(`Category ${editingCategory ? 'updated' : 'added'} successfully`);
                setIsModalVisible(false);
                fetchCategories(pagination.currentPage, pagination.itemsPerPage);
            } else {
                message.error(response.message || `Failed to ${editingCategory ? 'update' : 'add'} category`);
            }
        } catch (error) {
            console.error(`Failed to ${editingCategory ? 'update' : 'add'} category:`, error);
            message.error(`Failed to ${editingCategory ? 'update' : 'add'} category`);
        } finally {
            setLoading(false);
        }
    };

    const handleSeedDefaultCategories = async () => {
        try {
            setLoading(true);
            const response = await seedDefaultCategoriesAdmin({ token });

            if (response.success) {
                message.success(response.message || "Default categories seeded successfully");
                fetchCategories(pagination.currentPage, pagination.itemsPerPage);
            } else {
                message.error(response.message || "Failed to seed default categories");
            }
        } catch (error) {
            console.error("Failed to seed default categories:", error);
            message.error("Failed to seed default categories");
        } finally {
            setLoading(false);
        }
    };

    const handleColorChange = (color) => {
        setSelectedColor(color.toHexString());
        setColorPickerVisible(false);
    };

    const paginationConfig = {
        current: pagination.currentPage,
        pageSize: pagination.itemsPerPage,
        total: pagination.totalItems,
        onChange: (page, pageSize) => {
            fetchCategories(page, pageSize);
        }
    };

    const columns = [
        {
            title: "#",
            dataIndex: "index",
            key: "index",
            width: 50,
            render: (_, __, index) => (pagination.currentPage - 1) * pagination.itemsPerPage + index + 1
        },
        {
            title: "Color",
            dataIndex: "color",
            key: "color",
            width: 60,
            render: (color) => (
                <div style={{
                    backgroundColor: color || '#1890ff',
                    width: '20px',
                    height: '20px',
                    borderRadius: '4px',
                    margin: '0 auto'
                }} />
            )
        },
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            width: 120,
        },
        {
            title: "Transaction Type",
            dataIndex: "transactionType",
            key: "transactionType",
            width: 120,
            render: (type) => {
                if (!type) return <Tag color="default">N/A</Tag>;
                const t = type.toLowerCase();
                if (t === "expense") return <Tag color="red">Expense</Tag>;
                if (t === "income") return <Tag color="green">Income</Tag>;
                if (t === "transfer") return <Tag color="blue">Transfer</Tag>;
                return <Tag>{type}</Tag>;
            }
        },
        {
            title: "Created By",
            dataIndex: "userId",
            key: "userId",
            width: 100,
            render: (userId) => userId ? (
                <Tooltip title={userId.email}>
                    {userId.displayName || userId.username || userId.email}
                </Tooltip>
            ) : "System"
        },
        {
            title: "Created At",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 120,
            render: (createdAt) => createdAt ? moment(createdAt).fromNow() : "N/A"
        },
        {
            title: "Description",
            dataIndex: "description",
            key: "description",
            ellipsis: true,
            width: 160,
            render: (description) => description ? description : "No description"
        },
        {
            title: "Order",
            dataIndex: "order",
            key: "order",
            width: 80,
            render: (order) => order || 0,
            sorter: (a, b) => (a.order || 0) - (b.order || 0),
        },
        {
            title: "Updated At",
            dataIndex: "updatedAt",
            key: "updatedAt",
            width: 120,
            render: (updatedAt) => {
                if (!updatedAt) return "N/A";
                const now = moment();
                const updated = moment(updatedAt);
                const diffHours = now.diff(updated, 'hours', true);
                const diffDays = now.diff(updated, 'days', true);
                let color = undefined;
                if (diffHours < 12) {
                    color = 'green';
                } else if (diffDays < 1) {
                    color = 'orange';
                }
                return (
                    <span style={color ? { color } : {}}>
                        {updated.fromNow()}
                    </span>
                );
            }
        },
        {
            title: "Synced At",
            dataIndex: "syncedAt",
            key: "syncedAt",
            width: 120,
            render: (syncedAt) => {
                if (!syncedAt) return <span style={{ color: '#999' }}>Never</span>;
                const now = moment();
                const synced = moment(syncedAt);
                const diffHours = now.diff(synced, 'hours', true);
                const diffDays = now.diff(synced, 'days', true);

                let color = undefined;
                if (diffHours < 1) {
                    color = '#52c41a'; // Green for recent sync
                } else if (diffHours < 24) {
                    color = '#fa8c16'; // Orange for same day
                } else if (diffDays < 7) {
                    color = '#722ed1'; // Purple for this week
                } else {
                    color = '#ff4d4f'; // Red for old sync
                }

                return (
                    <Tooltip title={`Last synced: ${synced.format('YYYY-MM-DD HH:mm:ss')}`}>
                        <span style={{ color }}>
                            {synced.fromNow()}
                        </span>
                    </Tooltip>
                );
            },
            sorter: (a, b) => {
                if (!a.syncedAt && !b.syncedAt) return 0;
                if (!a.syncedAt) return 1;
                if (!b.syncedAt) return -1;
                return moment(b.syncedAt).valueOf() - moment(a.syncedAt).valueOf();
            },
        },
        {
            title: "Actions",
            key: "actions",
            width: 120,
            render: (_, record) => (
                <Space>
                    <Tooltip title="View Details">
                        <Button
                            icon={<EyeOutlined />}
                            size="small"
                            onClick={() => {
                                setDetailCategory(record);
                                setDetailModalVisible(true);
                            }}
                        />
                    </Tooltip>
                    <Tooltip title="Edit Category">
                        <Button
                            icon={<EditOutlined />}
                            type="primary"
                            size="small"
                            onClick={() => handleEditCategory(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Delete Category">
                        <Popconfirm
                            title="Are you sure you want to delete this category?"
                            description="This action cannot be undone."
                            onConfirm={() => handleDeleteCategory(record._id)}
                            okText="Yes"
                            cancelText="No"
                            placement="left"
                        >
                            <Button
                                icon={<DeleteOutlined />}
                                danger
                                size="small"
                            />
                        </Popconfirm>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <Card title="Category Management" className="category-page-card">
            <Space style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <Space>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddCategory}
                    >
                        Add Category
                    </Button>

                    <Button
                        type="default"
                        icon={<RestOutlined />}
                        onClick={handleSeedDefaultCategories}
                        loading={loading}
                    >
                        Seed Default Categories
                    </Button>
                </Space>

                <Button
                    type="default"
                    icon={<SyncOutlined />}
                    onClick={() => fetchCategories(pagination.currentPage, pagination.itemsPerPage)}
                >
                    Refresh Data
                </Button>
            </Space>

            <Table
                columns={columns}
                dataSource={categories}
                rowKey="_id"
                loading={loading}
                pagination={paginationConfig}
                size="middle"
                className="category-table"
            />

            {/* Add/Edit Category Modal */}
            <Modal
                title={`${editingCategory ? 'Edit' : 'Add'} Category`}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={null}
            >
                <Form
                    form={categoryForm}
                    layout="vertical"
                    onFinish={handleSaveCategory}
                >
                    <Form.Item
                        name="name"
                        label="Category Name"
                        rules={[{ required: true, message: 'Please enter a category name' }]}
                    >
                        <Input placeholder="Enter category name" />
                    </Form.Item>

                    <Form.Item
                        name="transactionType"
                        label="Transaction Type"
                        rules={[{ required: true, message: 'Please select a transaction type' }]}
                    >
                        <Select
                            placeholder="Select transaction type"
                            options={transactionTypes}
                        />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description"
                    >
                        <Input.TextArea rows={3} placeholder="Enter category description (optional)" />
                    </Form.Item>

                    <Form.Item
                        name="iconCode"
                        label="Icon Code"
                        extra="Ví dụ: restaurant, shopping_bag, directions_car"
                    >
                        <Input placeholder="Enter icon code (optional)" />
                    </Form.Item>

                    {/* Translations fields */}
                    <div style={{ marginBottom: 16 }}>
                        <label style={{ fontWeight: 500 }}>Translations</label>
                        {translationFields.map((field, idx) => (
                            <div key={field.lang} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                                <Select
                                    value={field.lang}
                                    style={{ width: 120 }}
                                    disabled
                                    options={languageOptions}
                                />
                                <Input
                                    placeholder="Name"
                                    value={field.name}
                                    onChange={e => {
                                        const newFields = [...translationFields];
                                        newFields[idx].name = e.target.value;
                                        setTranslationFields(newFields);
                                    }}
                                    style={{ width: 140 }}
                                />
                                <Input
                                    placeholder="Description"
                                    value={field.description}
                                    onChange={e => {
                                        const newFields = [...translationFields];
                                        newFields[idx].description = e.target.value;
                                        setTranslationFields(newFields);
                                    }}
                                    style={{ width: 180 }}
                                />
                            </div>
                        ))}
                    </div>

                    <Form.Item
                        name="order"
                        label="Display Order"
                        rules={[{
                            validator: (_, value) => {
                                if (value === undefined || value === null || value === '') {
                                    return Promise.resolve();
                                }
                                const numValue = Number(value);
                                if (isNaN(numValue) || numValue < 0) {
                                    return Promise.reject(new Error('Order must be a number greater than or equal to 0'));
                                }
                                return Promise.resolve();
                            }
                        }]}
                    >
                        <Input
                            type="number"
                            placeholder="Enter display order (0 = first)"
                            min={0}
                        />
                    </Form.Item>

                    <Form.Item label="Color">
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div
                                style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '4px',
                                    backgroundColor: selectedColor,
                                    marginRight: '8px',
                                    border: '1px solid #d9d9d9',
                                    cursor: 'pointer'
                                }}
                                onClick={() => setColorPickerVisible(!colorPickerVisible)}
                            />
                            <Button
                                icon={<BgColorsOutlined />}
                                onClick={() => setColorPickerVisible(!colorPickerVisible)}
                            >
                                {colorPickerVisible ? 'Hide Color Picker' : 'Choose Color'}
                            </Button>
                        </div>
                        {colorPickerVisible && (
                            <div style={{ marginTop: 8 }}>
                                <ColorPicker
                                    value={selectedColor}
                                    onChange={handleColorChange}
                                    showText
                                />
                            </div>
                        )}
                    </Form.Item>

                    <div style={{ textAlign: 'right', marginTop: 16 }}>
                        <Space>
                            <Button onClick={() => setIsModalVisible(false)}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                {editingCategory ? 'Update' : 'Create'}
                            </Button>
                        </Space>
                    </div>
                </Form>
            </Modal>

            {/* Detail Modal */}
            <Modal
                title="Category Details"
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={null}
                width={600}
            >
                {detailCategory ? (
                    <div>
                        <div style={{ marginBottom: 12 }}>
                            <Tag color={detailCategory.color || '#1890ff'} style={{ marginRight: 8 }}>
                                {detailCategory.name}
                            </Tag>
                            <Tag>
                                {detailCategory.transactionType}
                            </Tag>
                        </div>
                        <div><b>ID:</b> {detailCategory._id}</div>
                        <div><b>Description:</b> {detailCategory.description || "No description"}</div>
                        <div><b>Icon Code:</b> {detailCategory.iconCode || "None"}</div>
                        <div><b>Order:</b> {detailCategory.order}</div>
                        <div><b>Created By:</b> {detailCategory.userId ? (detailCategory.userId.displayName || detailCategory.userId.username || detailCategory.userId.email) : "System"}</div>
                        <div><b>Created At:</b> {detailCategory.createdAt ? moment(detailCategory.createdAt).format("YYYY-MM-DD HH:mm:ss") : "N/A"}</div>
                        <div><b>Updated At:</b> {detailCategory.updatedAt ? moment(detailCategory.updatedAt).format("YYYY-MM-DD HH:mm:ss") : "N/A"}</div>
                        <div><b>Synced At:</b> {detailCategory.syncedAt ? moment(detailCategory.syncedAt).format("YYYY-MM-DD HH:mm:ss") : "Never"}</div>
                        <div><b>Is Default:</b> {detailCategory.isDefault ? "Yes" : "No"}</div>
                        <div><b>Is Deleted:</b> {detailCategory.isDeleted ? "Yes" : "No"}</div>
                        <div style={{ marginTop: 12 }}>
                            <b>Translations:</b>
                            {detailCategory.translations && Object.keys(detailCategory.translations).length > 0 ? (
                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                    {Object.entries(detailCategory.translations).map(([lang, val]) => {
                                        const v = val as { name?: string; description?: string } | undefined;
                                        return (
                                            <li key={lang}>
                                                <b>{lang}:</b> {v?.name || ""} {v?.description ? `- ${v.description}` : ""}
                                            </li>
                                        );
                                    })}
                                </ul>
                            ) : (
                                <span> None</span>
                            )}
                        </div>
                    </div>
                ) : null}
            </Modal>
        </Card>
    );
};

export default DefaultCategoryPage;
